'use strict';

import { spellsOut } from './phone-align.js';
import { splitWords } from './norm.js';
import { candidates } from './morphology.js';

export const CMU_DICT_URL = 'https://raw.githubusercontent.com/cmusphinx/cmudict/master/cmudict.dict';
export const CMU_DICT_IDB_KEY = 'cmu_dict_decoded';
export const CMU_DICT_SIZE_KEY = 'cmu_dict_size';
export const CMU_SUPPLEMENT_IDB_KEY = 'cmu_supplement_decoded';
export const CMU_SUPPLEMENT_SIZE_KEY = 'cmu_supplement_size';

let _idbGet = null;
let _idbPut = null;
let _supplementUrl = null;

export function configureIO({ idbGet, idbPut, supplementUrl }) {
  _idbGet = idbGet;
  _idbPut = idbPut;
  _supplementUrl = supplementUrl;
}

export const cmuSupplementUrl = () => _supplementUrl;

// A word's pronunciations are stored as one string, phones space-joined and
// variants |-joined ('R EH1 D|R IY1 D'), not as arrays: structured-clone
// de-interns arrays across the IDB round-trip, and an array per word costs CMU
// and the supplement 14 MB of heap between them.
function pronouncingDict(url, dataKey, sizeKey) {
  let dict = null;
  let loading = null;
  return {
    get: () => dict,
    has: () => !!dict,
    set(map) {
      dict = map;
      loading = null;
    },
    invalidate() {
      dict = null;
      loading = null;
    },
    async load() {
      if (dict) return;
      if (loading) return loading;
      loading = (async () => {
        // The field names the record's shape: a cache in an older one reads as a
        // miss and is fetched over, never misread.
        const cached = await _idbGet(dataKey);
        if (cached && cached.joined) {
          dict = cached.joined;
          return;
        }
        const resp = await fetch(url());
        if (!resp.ok) throw new Error(`HTTP ${resp.status} ${resp.statusText}`);
        dict = parseCmuDict(await resp.text());
        await _idbPut(dataKey, { joined: dict });
        const size = resp.headers.get('content-length');
        if (size) await _idbPut(sizeKey, size);
      })();
      try {
        await loading;
      } finally {
        if (!dict) loading = null;
      }
    },
  };
}

export const cmuBase = pronouncingDict(() => CMU_DICT_URL, CMU_DICT_IDB_KEY, CMU_DICT_SIZE_KEY);

export const cmuSupplement = pronouncingDict(() => _supplementUrl, CMU_SUPPLEMENT_IDB_KEY, CMU_SUPPLEMENT_SIZE_KEY);

// A listed word replaces CMU's whole list: merge the two and a bad CMU reading
// survives the fix meant to remove it.
const pronsOf = key => (cmuSupplement.get().get(key) ?? cmuBase.get().get(key))?.split('|');

export function hasCmuDict() { return cmuBase.has() && cmuSupplement.has(); }

export async function loadCmuDict() {
  await Promise.all([cmuBase.load(), cmuSupplement.load()]);
}

const toMap = obj => new Map([...(obj instanceof Map ? obj : Object.entries(obj))]
  .map(([key, prons]) => [key, Array.isArray(prons) ? prons.join('|') : prons]));

export function setCmuDict(map, supplement = {}) {
  cmuBase.set(toMap(map));
  cmuSupplement.set(toMap(supplement));
}

const cmuKey = word => word.toUpperCase().replace(/[^A-Z]/g, '');

const lastWord = text => splitWords(text).at(-1) ?? '';

// The cot–caught merger, deliberate: CMU records the AO/AA split arbitrarily —
// CAUGHT both ways, GONE only AO, DON only AA, ON both — so honoring it tears one
// family in half along a coin flip in the dictionary, GONE PRO off DON HO with ON
// AUX left in each. Never before R, where no dialect merges them and folding
// rhymes CARD with CORD, BARB with ORB, CARD PLAYERS with BOARD GAMERS.
// Before R, CMU splits IY from IH and UW from UH just as arbitrarily — HERE IY R
// but NEAR IH R, POOR UW R but TOUR UH R — so those fold too, or HERE stops
// rhyming with NEAR and BIER can't find BEER. The SQUARE vowel too, EY into EH:
// SHARE is EH R but PLOWSHARE EY R. Only on an R that closes the syllable — before
// a vowel the R starts the next one, and PAYROLL would read as "pehroll".
const BEFORE_R = { IY: 'IH', UW: 'UH', EY: 'EH' };
const foldVowel = (toks, i, beforeR = true) => {
  const phone = toks[i];
  const bare = phone.slice(0, 2);
  if (toks[i + 1] === 'R') {
    const folds = beforeR && bare in BEFORE_R && !(bare === 'EY' && isVowel(toks[i + 2] ?? '-'));
    return folds ? BEFORE_R[bare] + phone.slice(2) : phone;
  }
  return bare === 'AO' ? 'AA' + phone.slice(2) : phone;
};

// CMU writes the R after AY and AW as its own syllable or not on a whim — TIRE
// AY ER, SPIRE AY R, WIRE both — so that ER folds into R, or SPIRE doesn't rhyme
// with TIRE and PYRE can't find PIRATE.
const smoothR = pron => pron.replace(/\b(A[YW]\d) ER0\b/g, '$1 R');

// 'loose' anchors the rhyme on the last stress of either rank, 'strict' only on the
// primary — so cumberbatch's secondary -batch rhymes with match under loose, not strict.
// Stress stripped so a secondary anchor still matches a primary under loose (dynamite ~ kite).
export function rhymingPart(pron, mode = 'loose') {
  const toks = flapT(smoothR(pron)).split(' ');
  let start = -1;
  for (let i = toks.length - 1; i >= 0; i--) {
    const last = toks[i][toks[i].length - 1];
    if (last === '1' || (mode !== 'strict' && last === '2')) { start = i; break; }
  }
  const tail = start >= 0 ? toks.slice(start) : toks;
  return tail.map((ph, i) => {
    const folded = foldVowel(tail, i);
    return isWeak(folded) ? 'AX' : folded;
  }).join(' ').replace(/\d/g, '');
}

export function rhymingPartsOf(text, mode = 'loose') {
  if (!hasCmuDict()) return [];
  if (mode === 'whole') return wholeEntryParts(text);
  const prons = pronsOf(cmuKey(lastWord(text)));
  if (!prons) return [];
  const parts = new Set();
  for (const pron of prons) parts.add(rhymingPart(pron, mode));
  return [...parts];
}

// ─── Syllables & whole-entry rhyme ───────────────────────────────────────────

// Onsets English allows word-initially, kept to the native clusters. CMU also
// attests loanword ones (SH W, S V, K N, T S); admitting them would pull a
// consonant off a coda word-internally — ASH-ley read as A-shley — silently
// dropping a coda the whole-entry rhyme is keyed on.
const LEGAL_ONSETS = new Set([
  'P R', 'B R', 'T R', 'D R', 'K R', 'G R',
  'P L', 'B L', 'K L', 'G L',
  'T W', 'D W', 'K W', 'G W',
  'P Y', 'B Y', 'T Y', 'D Y', 'K Y', 'G Y',
  'F R', 'F L', 'TH R', 'SH R', 'TH W', 'HH W',
  'F Y', 'V Y', 'HH Y', 'M Y', 'N Y',
  'S P', 'S T', 'S K', 'S M', 'S N', 'S L', 'S W', 'S F',
  'S P R', 'S T R', 'S K R', 'S P L', 'S K W', 'S K Y', 'S P Y', 'S T Y',
]);

const isVowel = ph => 'AEIOU'.includes(ph[0]);

// The checked (lax) vowels, which English does not let end a stressed syllable:
// BAREST has no /ˈbɛ.rəst/ reading, only /ˈbɛr.əst/. Maximal Onset alone would hand
// that R to the next onset, where the key drops it — collapsing every
// C-EH-C-schwa-S-T word onto one key and rhyming BAREST with CHEMIST and DENTIST.
const CHECKED = new Set(['AA', 'AE', 'AH', 'AO', 'EH', 'IH', 'UH']);

function isCheckedStressed(nucleus) {
  const stress = nucleus[nucleus.length - 1];
  return stress > '0' && stress <= '9' && CHECKED.has(nucleus.slice(0, -1));
}

function splitCluster(cons, nucleus) {
  const least = cons.length && isCheckedStressed(nucleus) ? 1 : 0;
  for (let i = least; i <= cons.length; i++) {
    const onset = cons.slice(i);
    if (onset.length <= 1 || LEGAL_ONSETS.has(onset.join(' '))) return [cons.slice(0, i), onset];
  }
  return [cons, []];
}

// Maximal Onset over ONE word's pronunciation. Per word, never across a boundary:
// let it span one and D R is a legal onset, so ROAD RAGE reads as roa-drage and
// stops rhyming with CODE PAGE.
export function syllabify(pron) {
  const toks = pron.split(' ');
  const vowels = [];
  for (let i = 0; i < toks.length; i++) if (isVowel(toks[i])) vowels.push(i);
  if (!vowels.length) return [];
  const sylls = [];
  let onset = toks.slice(0, vowels[0]);
  for (let s = 0; s < vowels.length; s++) {
    const v = vowels[s];
    const nextV = vowels[s + 1];
    let coda, nextOnset;
    if (nextV === undefined) { coda = toks.slice(v + 1); nextOnset = []; }
    else [coda, nextOnset] = splitCluster(toks.slice(v + 1, nextV), toks[v]);
    sylls.push({ onset, nucleus: toks[v], coda });
    onset = nextOnset;
  }
  return sylls;
}

// Unstressed vowels neutralize toward schwa, which is what marries ANNE BOLEYN's
// OW0 to MANDOLIN's AH0. ER0, IY0, UW0 and the diphthongs resist reduction and
// stay distinct — collapse those too and any two entries sharing a stress shape
// match, CZECHOSLOVAKIA against ENERGY POLICY.
const REDUCIBLE = new Set(['AA', 'AE', 'AH', 'AO', 'EH', 'IH', 'OW', 'UH']);

// Rhyming a last word, or hearing a word inside an entry, an unstressed OW keeps
// its "oh": CMU writes a reduced O as AH, so OW0 is a full vowel, and reducing it
// has BELLOW find BELLA and AVOCADO rhyme with PRADA.
const isWeak = vowel => vowel.endsWith('0') && vowel.slice(0, 2) !== 'OW' && REDUCIBLE.has(vowel.slice(0, -1));

// CMU writes ACHOO's first vowel AA2, a full "ah" that MASSACHUSETTS (AH0) and
// STATUE (AE1) would never otherwise meet. Only a secondary "ah" or "uh": widen an
// unstressed one too and TUBA finds TOO BAD; a secondary I and INSULT finds CONSULT.
const AH_VOWELS = ['AA', 'AE', 'AH'];
export function looseVowelsOf(phone, stress) {
  return stress === '2' && AH_VOWELS.includes(phone) ? [...AH_VOWELS, 'AX'] : null;
}

// Own coda, never the next syllable's onset: SAW RED has to keep folding, since a
// word-initial R colors nothing before it.
function nucleusKey(vowel, coda) {
  const folded = foldVowel([vowel, ...coda], 0);
  const stress = folded[folded.length - 1];
  if (stress < '0' || stress > '9') return folded;
  const bare = folded.slice(0, -1);
  return stress === '0' && REDUCIBLE.has(bare) ? 'AX' : bare;
}

const MAX_WHOLE_KEYS = 16;

// A coda repeating the next onset is held once, not twice — TIME MACHINE is said
// ti-ma-chine, which is what lets it meet LIMA BEAN. Both readings are kept: drop
// the undegeminated one and CLIMB A BEAN stops meeting TIME MACHINE.
function syllableKeys(sylls) {
  let variants = [[]];
  for (let i = 0; i < sylls.length; i++) {
    const { nucleus, coda } = sylls[i];
    const nextOnset = sylls[i + 1]?.onset || [];
    const nuc = nucleusKey(nucleus, coda);
    const forms = [nuc + (coda.length ? ' ' + coda.join(' ') : '')];
    if (coda.length && nextOnset.length && coda[coda.length - 1] === nextOnset[0]) {
      const held = coda.slice(0, -1);
      forms.push(nuc + (held.length ? ' ' + held.join(' ') : ''));
    }
    const next = [];
    for (const v of variants) {
      for (const f of forms) if (next.length < MAX_WHOLE_KEYS) next.push(v.concat(f));
    }
    variants = next;
  }
  return variants.map(v => v.join(' | '));
}

// Every syllable of the entry rhymes with its counterpart, onsets free throughout,
// so a 3-syllable phrase can meet a 3-syllable word however their words divide up.
// One unreadable word and there is no reading at all — unlike the last-word modes,
// which only ever needed the tail.
function wholeEntryParts(text) {
  const words = splitWords(text);
  let combos = [[]];
  for (const word of words) {
    const prons = pronsOf(cmuKey(word));
    if (!prons) return [];
    const next = [];
    for (const c of combos) {
      for (const p of prons) if (next.length < MAX_WHOLE_KEYS) next.push(c.concat(p));
    }
    combos = next;
  }
  const keys = new Set();
  for (const combo of combos) {
    const sylls = [];
    for (const pron of combo) sylls.push(...syllabify(flapT(smoothR(pron))));
    if (sylls.length) for (const key of syllableKeys(sylls)) keys.add(key);
  }
  return [...keys];
}

// ─── Sound strings ───────────────────────────────────────────────────────────

// Exactly one code unit per phone: with wider codes, a match could land mid-phone
// and find sounds that aren't there.
const phoneCodes = new Map();
export const phoneCode = phone => {
  let code = phoneCodes.get(phone);
  if (code === undefined) phoneCodes.set(phone, code = String.fromCharCode(0x41 + phoneCodes.size));
  return code;
};

const SOUND_CACHE_MAX = 200_000;

function wordLookup(build, { inflect = false } = {}) {
  let cache = new Map();
  let cacheBase = null;
  let cacheSupplement = null;
  return word => {
    if (!hasCmuDict()) return null;
    if (cacheBase !== cmuBase.get() || cacheSupplement !== cmuSupplement.get() || cache.size > SOUND_CACHE_MAX) {
      cache = new Map();
      cacheBase = cmuBase.get();
      cacheSupplement = cmuSupplement.get();
    }
    const key = cmuKey(word);
    let found = cache.get(key);
    if (found !== undefined) return found;
    const prons = key ? pronsOf(key) : null;
    const said = prons ? citationProns(key, prons) : key && inflect ? inflectedProns(key) : null;
    found = said ? build(said) : null;
    cache.set(key, found);
    return found;
  };
}

// ─── Inflections CMU lacks ───────────────────────────────────────────────────

const VOICELESS = new Set(['P', 'T', 'K', 'F', 'TH', 'S', 'SH', 'CH']);
const HISSING = new Set(['S', 'Z', 'SH', 'ZH', 'CH', 'JH']);

function endingAfter(word, last) {
  if (word.endsWith('ing')) return 'IH0 NG';
  if (word.endsWith('d')) return last === 'T' || last === 'D' ? 'IH0 D' : VOICELESS.has(last) ? 'T' : 'D';
  return HISSING.has(last) ? 'IH0 Z' : VOICELESS.has(last) ? 'S' : 'Z';
}

// The longest stem first: DIMED is DIME plus D, not DIM plus D.
function inflectedProns(key) {
  const word = key.toLowerCase();
  const stems = [...candidates(word)].filter(stem => stem !== word).sort((a, b) => b.length - a.length);
  for (const stem of stems) {
    const prons = pronsOf(cmuKey(stem));
    if (!prons) continue;
    return citationProns(cmuKey(stem), prons).map(pron => {
      const last = pron.slice(pron.lastIndexOf(' ') + 1).replace(/\d/, '');
      return `${pron} ${endingAfter(word, last)}`;
    });
  }
  return null;
}

// CMU's weak forms of common words, stress stripped. Each is a sound or two found
// all over the wordlist, so searching the word would flood: ARE as "er" finds every
// BUTCHER, TO as "tuh" every SELECTED. Weak forms missing here are kept on purpose:
// A as "uh" and THE as "thee" are how the words are ordinarily said, and a phrase
// searched with them has to meet A LOT and THE APPLE.
const WEAK_FORMS = {
  ARE: ['ER'], OR: ['ER'], FOR: ['F ER', 'F R ER'], TO: ['T IH', 'T AH'], INTO: ['IH N T AH'],
  AND: ['AH N D'], HIM: ['IH M'], THAT: ['DH AH T'], THAN: ['DH AH N'], THEM: ['DH AH M'],
  WAS: ['W AH Z'], CAN: ['K AH N'], WILL: ['W AH L'], HAS: ['HH AH Z'], BEEN: ['B AH N'],
  JUST: ['JH IH S T'], DOES: ['D IH Z'], GOOD: ['G IH D'], YEARS: ['Y ER Z'], YOURS: ['Y ER Z'],
};

// Only the pronunciations a reader would say for the word: its weak forms and any
// abbreviation expansion (CO. as "company", which would hear KNEE in AND CO) go,
// as long as something is left.
function citationProns(key, prons) {
  if (prons.length < 2) return prons;
  const weak = WEAK_FORMS[key];
  let kept = weak ? prons.filter(pron => !weak.includes(pron.replace(/\d/g, ''))) : prons;
  if (!kept.length) kept = prons;
  if (kept.length > 1) {
    const word = key.toLowerCase();
    const spelled = kept.filter(pron => spellsOut(word, pron.replace(/\d/g, '').split(' ')));
    if (spelled.length) kept = spelled;
  }
  return kept;
}

// The American flap: a T between a vowel (or R) and an unstressed vowel is said
// as D, so PUTTING finds PUDDING and METAL finds MEDAL. Not before a syllabic N,
// where it's a glottal stop: BURTON isn't BURDEN.
const flapT = pron => {
  const toks = pron.split(' ');
  const flaps = i => (isVowel(toks[i - 1] ?? '-') || toks[i - 1] === 'R') && toks[i + 1]?.endsWith('0')
    && !(toks[i + 2] === 'N' && !(toks[i + 3] && isVowel(toks[i + 3])));
  return toks.map((ph, i) => (ph === 'T' && flaps(i) ? 'D' : ph)).join(' ');
};

// Yod coalescence across a word break: GOT YOU is said "gotchoo", CATCH YOU "catchoo".
// Only into YOU and its forms, which are said unstressed; a stressed Y word keeps its
// Y, or JAW finds SCOTLAND YARD.
const INTO_Y = { T: 'CH', D: 'JH', S: 'SH', Z: 'ZH', CH: 'CH', JH: 'JH', SH: 'SH', ZH: 'ZH' };
const YOU_FORMS = new Set(['you', 'your', 'youre', 'yours', 'yourself', 'yourselves', 'youd', 'youll', 'youve']);
export const coalescedWithY = (phone, word) => (YOU_FORMS.has(word) ? INTO_Y[phone] ?? null : null);

// Stress stripped: `arbiter`'s unstressed -biter has to meet `bitter`.
// Each word is read plain and folded, and Phone search pairs plain with plain and
// folded with folded. The folds only see inside the word, so THE RACE keeps its
// IY and would lose ERASE's IY R if the plain reading went. The folded reading's
// code also reduces weak unstressed vowels to one schwa, so ROCKET finds
// CROCKETT; its phones stay unreduced for highlighting, which has no letters for AX.
export const soundsOf = wordLookup(soundsOfProns, { inflect: true });

function soundsOfProns(prons) {
  const byCode = new Map();
  for (const raw of prons) {
    for (const folded of [false, true]) {
      const pron = folded ? flapT(smoothR(raw)) : raw;
      const toks = pron.split(' ');
      const bare = pron.replace(/\d/g, '').split(' ');
      const phones = bare.map((ph, i) => foldVowel(bare, i, folded));
      const keys = folded ? phones.map((ph, i) => (isWeak(ph + toks[i].slice(-1)) ? 'AX' : ph)) : phones;
      const code = keys.map(phoneCode).join('');
      if (!byCode.has(code)) {
        const stress = toks.map(tok => (/\d$/.test(tok) ? tok.slice(-1) : '-')).join('');
        byCode.set(code, { phones, code, stress, plain: false, folded: false });
      }
      byCode.get(code)[folded ? 'folded' : 'plain'] = true;
    }
  }
  return [...byCode.values()];
}

// ─── Letter names ────────────────────────────────────────────────────────────

const LETTER_NAMES = {
  a: 'EY1', b: 'B IY1', c: 'S IY1', d: 'D IY1', e: 'IY1', f: 'EH1 F', g: 'JH IY1', h: 'EY1 CH', i: 'AY1',
  j: 'JH EY1', k: 'K EY1', l: 'EH1 L', m: 'EH1 M', n: 'EH1 N', o: 'OW1', p: 'P IY1', q: 'K Y UW1', r: 'AA1 R',
  s: 'EH1 S', t: 'T IY1', u: 'Y UW1', v: 'V IY1', w: 'D AH1 B AH0 L Y UW0', x: 'EH1 K S', y: 'W AY1', z: 'Z IY1',
};

let spelledCache = new Map();

// `plural` because the letters are already lowercase: MVPs and MVPS look alike here.
export function spelledSoundsOf(letters, plural = false) {
  const key = plural ? letters + '+' : letters;
  let found = spelledCache.get(key);
  if (found !== undefined) return found;
  if (spelledCache.size > SOUND_CACHE_MAX) spelledCache = new Map();
  const named = [...(plural ? letters.slice(0, -1) : letters)].map(ch => LETTER_NAMES[ch]);
  let pron = named.join(' ');
  if (plural) pron += ' ' + endingAfter('s', pron.slice(pron.lastIndexOf(' ') + 1).replace(/\d/, ''));
  found = named.includes(undefined) ? null : soundsOfProns([pron]);
  spelledCache.set(key, found);
  return found;
}

export const stressedSoundsOf = wordLookup(prons => {
  const seen = new Set();
  const sounds = [];
  for (const pron of prons) {
    const toks = pron.split(' ');
    const phones = toks.map((ph, i) => foldVowel(toks, i));
    const key = phones.join(' ');
    if (seen.has(key)) continue;
    seen.add(key);
    sounds.push({ phones });
  }
  return sounds;
});

export function lastWordKey(text) {
  return cmuKey(lastWord(text));
}

export function hasPronunciation(text) {
  return hasCmuDict() && !!pronsOf(cmuKey(lastWord(text)));
}

export function parseCmuDict(text) {
  const map = new Map();
  for (const line of text.split('\n')) {
    if (!line || line.startsWith(';;;')) continue;
    const hash = line.indexOf('#');
    const body = (hash >= 0 ? line.slice(0, hash) : line).trim();
    const sp = body.indexOf(' ');
    if (sp < 0) continue;
    const key = cmuKey(body.slice(0, sp).replace(/\(\d+\)$/, ''));
    const pron = body.slice(sp + 1).trim();
    if (!key || !pron) continue;
    let prons = map.get(key);
    if (!prons) map.set(key, prons = []);
    if (!prons.includes(pron)) prons.push(pron);
  }
  for (const [key, prons] of map) map.set(key, prons.join('|'));
  return map;
}
